/**
 * Mobile PWA — SSE client with auto-reconnect.
 *
 * 用原生 EventSource — 但 EventSource 不支持自定义 header（不能 set Authorization）。
 * 解决：用 fetch + ReadableStream 自实现（跟 desktop SseClient.ts 同模式）。
 *
 * 自动重连：disconnect 后 500ms → 30s 指数退避，无限重试。
 * 续传：客户端 record last seq，重连时 ?since= 拉补。
 */

import { DECK_BASE } from './api'
import { getToken } from './auth'

export interface SseEvent {
  /** v2: Redis Stream ID '<ms>-<seq>'; lex-comparable, monotonic. */
  stream_id: string
  session_id: string
  type: string
  payload: Record<string, unknown>
  origin: 'worker' | 'mobile' | 'system'
  event_id?: string | null
}

export interface SseOpenOptions {
  path: string  // 例 `/dispatch/sessions/${id}/events/stream`
  initialSince?: string  // v2: stream_id 字符串 ('0' = 从头)
  onEvent: (ev: SseEvent) => void
  onConnected?: () => void
  onDisconnected?: (reason: string) => void
}

export interface SseHandle {
  close: () => void
  isConnected: () => boolean
  getLastStreamId: () => string
}

export function openSse(opts: SseOpenOptions): SseHandle {
  let aborter = new AbortController()
  let running = true
  let connected = false
  let lastStreamId = opts.initialSince ?? '0'
  let backoffMs = 500

  async function loop(): Promise<void> {
    while (running) {
      try {
        await connectOnce()
      } catch (e) {
        connected = false
        opts.onDisconnected?.(String((e as Error)?.message || e))
      }
      if (!running) return
      const delay = backoffMs
      backoffMs = Math.min(backoffMs * 2, 30_000)
      await new Promise<void>((resolve) => {
        const t = setTimeout(resolve, delay + Math.random() * 500)
        aborter.signal.addEventListener('abort', () => {
          clearTimeout(t)
          resolve()
        }, { once: true })
      })
    }
  }

  async function connectOnce(): Promise<void> {
    aborter = new AbortController()
    const url = new URL(`${DECK_BASE}${opts.path}`)
    url.searchParams.set('since', lastStreamId)

    const token = getToken()
    const headers: Record<string, string> = { Accept: 'text/event-stream' }
    if (token) headers.Authorization = `Bearer ${token}`

    const res = await fetch(url, { headers, signal: aborter.signal })
    if (!res.ok) throw new Error(`SSE connect failed: ${res.status}`)

    connected = true
    backoffMs = 500
    opts.onConnected?.()

    const reader = res.body?.getReader()
    if (!reader) throw new Error('SSE body missing')
    const decoder = new TextDecoder('utf-8')
    let buf = ''
    let current: { event?: string; id?: string; data?: string } = {}

    try {
      for (;;) {
        const { done, value } = await reader.read()
        if (done) break
        buf += decoder.decode(value, { stream: true })
        let nl: number
        while ((nl = buf.indexOf('\n')) >= 0) {
          const line = buf.slice(0, nl).replace(/\r$/, '')
          buf = buf.slice(nl + 1)
          if (line === '') {
            if (current.data !== undefined) handleEvent(current)
            current = {}
            continue
          }
          if (line.startsWith(':')) continue
          const colon = line.indexOf(':')
          if (colon < 0) continue
          const field = line.slice(0, colon)
          const val = line.slice(colon + 1).trimStart()
          if (field === 'event') current.event = val
          else if (field === 'id') current.id = val
          else if (field === 'data') current.data = current.data ? `${current.data}\n${val}` : val
        }
      }
    } finally {
      connected = false
      opts.onDisconnected?.('stream-closed')
    }
  }

  function handleEvent(ev: { event?: string; id?: string; data?: string }): void {
    if (!ev.data) return
    let parsed: SseEvent
    try {
      parsed = JSON.parse(ev.data) as SseEvent
    } catch {
      return
    }
    // v2: 用 stream_id 字符串作为 cursor; lex compare 单调
    if (ev.id && ev.id > lastStreamId) {
      lastStreamId = ev.id
    } else if (typeof parsed.stream_id === 'string' && parsed.stream_id > lastStreamId) {
      lastStreamId = parsed.stream_id
    }
    try {
      opts.onEvent(parsed)
    } catch {
      // single-event handler error 不影响后续
    }
  }

  void loop()

  return {
    close: () => {
      running = false
      aborter.abort()
    },
    isConnected: () => connected,
    getLastStreamId: () => lastStreamId,
  }
}
